/* ============================================================
   Админка · Уведомления — отправка in-app + Web Push сообщений
   всей платформе или конкретному пользователю. Переиспользует
   store.sendNotification → POST /admin/notifications.
   ============================================================ */
import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../../store';
import { Ic } from '../icons';
import { AdmCard } from './ui';

const TYPES = [
  { k: 'info',    label: 'Инфо',   icon: 'info',  c: 'var(--fed-info)',          rgb: '59,165,217' },
  { k: 'success', label: 'Успех',  icon: 'check', c: 'var(--fed-ok)',            rgb: '67,217,106' },
  { k: 'warning', label: 'Важное', icon: 'bell',  c: 'var(--fed-gold)',          rgb: '230,183,60' },
  { k: 'alert',   label: 'Срочно', icon: 'zap',   c: 'var(--fed-accent-bright)', rgb: '225,18,23' },
];
const LINK_PRESETS = [
  { label: 'Уведомления', value: '#notifications' },
  { label: 'Турниры', value: '#tournaments' },
  { label: 'Новости', value: '#news' },
  { label: 'Эфир', value: '#live' },
];
const typeMeta = (k) => TYPES.find((t) => t.k === k) || TYPES[0];

function relTime(iso) {
  if (!iso) return 'только что';
  const d = Date.now() - new Date(iso).getTime();
  if (d < 60000) return 'только что';
  if (d < 3600000) return `${Math.floor(d / 60000)} мин назад`;
  if (d < 86400000) return `${Math.floor(d / 3600000)} ч назад`;
  try { return new Date(iso).toLocaleDateString('ru-RU'); } catch (e) { return String(iso).slice(0, 10); }
}

export default function AdmNotify() {
  const sendNotification = useAppStore((s) => s.sendNotification);
  const fetchUsers = useAppStore((s) => s.fetchUsers);
  const fetchSentNotifications = useAppStore((s) => s.fetchSentNotifications);

  const [users, setUsers] = useState([]);
  const [toMode, setToMode] = useState('all');   // 'all' | 'user'
  const [selUser, setSelUser] = useState(null);   // объект пользователя
  const [userQ, setUserQ] = useState('');
  const [type, setType] = useState('info');
  const [title, setTitle] = useState('');
  const [msg, setMsg] = useState('');
  const [link, setLink] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [sentLog, setSentLog] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // разово подгружаем всех пользователей для поиска получателя
  useEffect(() => {
    let alive = true;
    Promise.all([fetchUsers(''), fetchSentNotifications()])
      .then(([list, history]) => {
        if (!alive) return;
        setUsers(Array.isArray(list) ? list : []);
        setSentLog((history || []).map((item) => ({
          ...item,
          toLabel: item.to_label || (item.to === 'all' ? 'Все пользователи' : `ID ${item.to}`),
        })));
      })
      .catch(() => {})
      .finally(() => { if (alive) setHistoryLoading(false); });
    return () => { alive = false; };
  }, [fetchUsers, fetchSentNotifications]);

  const filteredUsers = useMemo(() => {
    const q = userQ.trim().toLowerCase();
    if (!q) return users.slice(0, 8);
    return users
      .filter((u) => (u.nickname || u.email || u.full_name || '').toLowerCase().includes(q))
      .slice(0, 12);
  }, [users, userQ]);

  const canSend = title.trim() && msg.trim() && (toMode === 'all' || selUser);
  const meta = typeMeta(type);

  const submit = async () => {
    if (!canSend || sending) return;
    setSending(true);
    try {
      const created = await sendNotification({
        to: toMode === 'all' ? 'all' : selUser.id,
        title: title.trim(),
        message: msg.trim(),
        type,
        link: link.trim() || undefined,
      });
      setSentLog((log) => [{
        id: created?.id ?? Date.now(),
        title: title.trim(),
        message: msg.trim(),
        type,
        toLabel: toMode === 'all' ? 'Все пользователи' : (selUser?.nickname || selUser?.email || `ID ${selUser?.id}`),
        created_at: created?.created_at || new Date().toISOString(),
      }, ...log].slice(0, 20));
      setDelivery(created?.delivery || null);
      setSent(true);
      setTitle(''); setMsg(''); setLink(''); setSelUser(null); setUserQ('');
      setTimeout(() => setSent(false), 2400);
    } catch (e) {
      /* ошибка уже показана тостом в store.sendNotification */
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fed-admin-view grid gap-5 items-start grid-cols-1 min-[1040px]:grid-cols-2">
      {/* ===== форма отправки ===== */}
      <AdmCard style={{ padding: 22 }}>
        <div className="font-mono uppercase text-fg-faint" style={{ fontSize: 10, letterSpacing: '.14em', marginBottom: 18 }}>
          Новое уведомление
        </div>

        {/* получатель */}
        <div style={{ marginBottom: 20 }}>
          <div className="fed-label" style={{ marginBottom: 9 }}>Получатель</div>
          <div className="flex gap-2" style={{ marginBottom: 12 }}>
            {[['all', 'Все'], ['user', 'Один пользователь']].map(([k, l]) => (
              <button
                key={k}
                onClick={() => { setToMode(k); if (k === 'all') { setSelUser(null); setUserQ(''); } }}
                className="flex-1 rounded-[10px] cursor-pointer font-display font-semibold uppercase border"
                style={{
                  padding: '10px 8px', fontSize: 12, letterSpacing: '.03em',
                  color: toMode === k ? 'var(--fed-fg)' : 'var(--fed-fg-muted)',
                  borderColor: toMode === k ? 'var(--fed-line-accent)' : 'var(--fed-line)',
                  background: toMode === k ? 'rgba(225,18,23,.08)' : 'transparent',
                }}
              >
                {l}
              </button>
            ))}
          </div>

          {toMode === 'user' && (
            <div className="relative">
              <input
                className="fed-input"
                placeholder="Поиск: никнейм или почта…"
                value={userQ}
                onChange={(e) => { setUserQ(e.target.value); setSelUser(null); }}
                style={{ borderColor: selUser ? 'var(--fed-ok)' : undefined }}
              />
              {userQ && !selUser && filteredUsers.length > 0 && (
                <div
                  className="absolute left-0 right-0 z-50 overflow-hidden rounded-xl border border-line"
                  style={{ top: 'calc(100% + 5px)', background: 'var(--fed-surface-2)', boxShadow: '0 20px 50px -18px rgba(0,0,0,.7)', maxHeight: 260, overflowY: 'auto' }}
                >
                  {filteredUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => { setSelUser(u); setUserQ(u.nickname || u.email || `ID ${u.id}`); }}
                      className="flex w-full items-center justify-between border-b border-line cursor-pointer text-left"
                      style={{ padding: '10px 13px', background: 'transparent' }}
                    >
                      <span className="truncate text-fg" style={{ fontSize: 13.5 }}>{u.nickname || u.email || '—'}</span>
                      <span className="font-mono text-fg-faint flex-shrink-0" style={{ fontSize: 10, marginLeft: 8 }}>ID {u.id}</span>
                    </button>
                  ))}
                </div>
              )}
              {selUser && (
                <div className="font-mono" style={{ fontSize: 11, color: 'var(--fed-ok)', marginTop: 7, letterSpacing: '.06em' }}>
                  <span className="inline-flex items-center gap-1.5"><Ic name="check" size={13} sw={2.4} /> Выбран: {selUser.nickname || selUser.email || `ID ${selUser.id}`}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* тип */}
        <div style={{ marginBottom: 20 }}>
          <div className="fed-label" style={{ marginBottom: 9 }}>Тип</div>
          <div className="flex flex-wrap gap-2">
            {TYPES.map((nt) => (
              <button
                key={nt.k}
                onClick={() => setType(nt.k)}
                className="rounded-full cursor-pointer font-display font-bold uppercase border"
                style={{
                  padding: '7px 14px', fontSize: 12, letterSpacing: '.05em',
                  borderColor: type === nt.k ? nt.c : 'var(--fed-line)',
                  background: type === nt.k ? `rgba(${nt.rgb},.14)` : 'transparent',
                  color: type === nt.k ? nt.c : 'var(--fed-fg-muted)',
                }}
              >
                <span className="inline-flex items-center gap-1.5"><Ic name={nt.icon} size={13} sw={2} />{nt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* заголовок */}
        <div style={{ marginBottom: 16 }}>
          <div className="fed-label" style={{ marginBottom: 8 }}>Заголовок</div>
          <input
            className="fed-input"
            placeholder="Например: Регистрация на турнир открыта"
            value={title}
            maxLength={80}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="font-mono text-fg-faint text-right" style={{ fontSize: 10, marginTop: 4 }}>{title.length}/80</div>
        </div>

        {/* текст */}
        <div style={{ marginBottom: 20 }}>
          <div className="fed-label" style={{ marginBottom: 8 }}>Текст</div>
          <textarea
            className="fed-input"
            placeholder="Текст уведомления…"
            value={msg}
            maxLength={400}
            rows={4}
            onChange={(e) => setMsg(e.target.value)}
            style={{ lineHeight: 1.6 }}
          />
          <div className="font-mono text-fg-faint text-right" style={{ fontSize: 10, marginTop: 4 }}>{msg.length}/400</div>
        </div>

        {/* ссылка */}
        <div style={{ marginBottom: 20 }}>
          <div className="fed-label" style={{ marginBottom: 8 }}>Ссылка (необязательно)</div>
          <input
            className="fed-input"
            placeholder="#tournament/mlbb-women-2026, #live или https://t.me/fc_sko/…"
            value={link}
            maxLength={500}
            onChange={(e) => setLink(e.target.value)}
          />
          <div className="flex flex-wrap gap-1.5" style={{ marginTop: 8 }}>
            {LINK_PRESETS.map((preset) => (
              <button key={preset.value} type="button" onClick={() => setLink(preset.value)}
                className="font-mono rounded-full border border-line cursor-pointer"
                style={{ padding: '5px 9px', background: link === preset.value ? 'rgba(225,18,23,.12)' : 'transparent', color: link === preset.value ? 'var(--fed-accent-bright)' : 'var(--fed-fg-faint)', fontSize: 9.5, letterSpacing: '.05em' }}>
                {preset.label}
              </button>
            ))}
          </div>
          <div className="text-fg-faint" style={{ fontSize: 11, marginTop: 5, lineHeight: 1.5 }}>
            Клик по уведомлению (и по push) откроет эту ссылку. Свой раздел сайта — «#live»,
            «#tournament/id», «#news»; внешняя ссылка — полный адрес с https://.
          </div>
        </div>

        {/* превью */}
        {(title || msg) && (
          <div
            className="rounded-xl"
            style={{ padding: '13px 16px', marginBottom: 20, background: `rgba(${meta.rgb},.08)`, border: `1px solid rgba(${meta.rgb},.32)`, borderLeft: `3px solid ${meta.c}` }}
          >
            <div className="font-mono uppercase" style={{ fontSize: 9.5, color: meta.c, letterSpacing: '.12em', marginBottom: 5 }}>
              Превью · {meta.label}
            </div>
            {title && <div className="font-display font-bold text-fg" style={{ fontSize: 15, marginBottom: msg ? 4 : 0 }}>{title}</div>}
            {msg && <div className="text-fg-soft" style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{msg}</div>}
          </div>
        )}

        <div className="flex items-center gap-3 rounded-xl border border-line" style={{ padding: '11px 13px', marginBottom: 14, background: 'rgba(255,255,255,.02)' }}>
          <span className="text-accent-bright"><Ic name="bell" size={19} /></span>
          <div>
            <div className="font-display font-semibold text-fg" style={{ fontSize: 12.5 }}>Два канала одной отправкой</div>
            <div className="text-fg-faint" style={{ fontSize: 11, marginTop: 2 }}>Лента уведомлений сайта и системный push на подключённые устройства.</div>
          </div>
        </div>

        <button
          onClick={submit}
          disabled={!canSend || sending}
          className="fed-btn-primary w-full gap-2"
          style={{ height: 50, fontSize: 14.5, cursor: canSend && !sending ? 'pointer' : 'not-allowed', opacity: canSend && !sending ? 1 : 0.5, background: sent ? 'var(--fed-ok)' : undefined }}
        >
          {sent ? (
            <><Ic name="check" size={17} sw={2.6} /> Отправлено</>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              {sending ? 'Отправляем…' : toMode === 'all' ? 'Отправить всем' : 'Отправить пользователю'}
            </>
          )}
        </button>
        {sent && delivery && (
          <div className="font-mono text-center text-fg-faint" style={{ fontSize: 10, marginTop: 9, letterSpacing: '.06em' }}>
            В ЛЕНТУ ДОБАВЛЕНО · PUSH-УСТРОЙСТВ: {delivery.webPush + delivery.devices}
          </div>
        )}
      </AdmCard>

      {/* ===== серверная история отправок ===== */}
      <div className="flex flex-col gap-3">
        <div className="font-mono uppercase text-fg-faint" style={{ fontSize: 10, letterSpacing: '.14em' }}>
          История отправок
        </div>
        {historyLoading ? (
          <AdmCard style={{ padding: '30px 20px' }}>
            <div className="text-fg-faint text-center" style={{ fontSize: 13 }}>Загружаем историю…</div>
          </AdmCard>
        ) : sentLog.length === 0 ? (
          <AdmCard style={{ padding: '30px 20px' }}>
            <div className="flex flex-col items-center gap-2.5 text-center">
              <span style={{ color: 'var(--fed-fg-faint)' }}><Ic name="bell" size={24} /></span>
              <div className="text-fg-faint" style={{ fontSize: 13 }}>Пока ничего не отправлено</div>
            </div>
          </AdmCard>
        ) : (
          sentLog.map((n) => {
            const m = typeMeta(n.type);
            return (
              <AdmCard key={n.id} style={{ padding: '13px 15px', borderLeft: `3px solid ${m.c}` }}>
                <div className="flex items-start justify-between gap-2" style={{ marginBottom: 6 }}>
                  <div className="font-display font-bold text-fg" style={{ fontSize: 14 }}>{n.title}</div>
                  <span className="font-mono text-fg-faint flex-shrink-0" style={{ fontSize: 10, letterSpacing: '.05em' }}>{relTime(n.created_at)}</span>
                </div>
                <div className="text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginBottom: 9, whiteSpace: 'pre-line' }}>{n.message}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono uppercase rounded-full" style={{ fontSize: 9.5, padding: '2px 8px', letterSpacing: '.08em', color: m.c, background: `rgba(${m.rgb},.16)` }}>
                    {m.label}
                  </span>
                  <span className="font-mono text-fg-faint inline-flex items-center gap-1" style={{ fontSize: 10, letterSpacing: '.06em' }}><Ic name="user" size={11} /> {n.toLabel}</span>
                </div>
              </AdmCard>
            );
          })
        )}
      </div>
    </div>
  );
}
